import { Outlet, useNavigate, NavLink, useLocation } from 'react-router-dom'
import { useAuthStore, useNotificationStore } from '@store'
import { useState, useRef, useEffect } from 'react'
import avatarPlaceholder from '@assets/images/image_01.png'
import { ROUTES } from '@constants'
import Footer from '@components/common/Footer'
import { conversationService } from '@services/api/messages'
import { notificationService } from '@services/api/notifications'
import { storageURL, mediaUrl } from '@config'
import publicApiClient from '@services/api/publicClient'
import { bindUserChannel } from '@services/pusher'
import toast from 'react-hot-toast'
import { encodeId } from '@utils/idCipher'
import DownloadAppModal from '@components/common/DownloadAppModal'

// Icône SVG + couleurs selon le type de notification
function renderNotifIcon(type) {
  const config = {
    like: {
      bg: 'bg-red-100', text: 'text-red-500',
      path: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
    },
    comment: {
      bg: 'bg-blue-100', text: 'text-blue-500',
      path: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
    },
    new_post: {
      bg: 'bg-yellow-100', text: 'text-[#D4AF37]',
      path: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z',
    },
  }
  const c = config[type] || {
    bg: 'bg-gray-100', text: 'text-gray-500',
    path: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
  }
  return (
    <div className={`w-10 h-10 ${c.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
      <svg className={`w-5 h-5 ${c.text}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d={c.path} />
      </svg>
    </div>
  )
}

// Formate une date en temps relatif (maintenant, 27min, 2h, 3j, 04 juin)
function formatRelativeTime(d) {
  if (!d) return ''
  const date = new Date(d)
  if (isNaN(date)) return ''
  const diff = Date.now() - date
  const m = Math.floor(diff / 60000)
  const h = Math.floor(diff / 3600000)
  const dy = Math.floor(diff / 86400000)
  if (m < 1) return 'maintenant'
  if (m < 60) return `${m}min`
  if (h < 24) return `${h}h`
  if (dy < 7) return `${dy}j`
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
}

function DashboardLayout() {
  const { user, logout, userType } = useAuthStore()
  const { unreadCount, messagesUnreadCount, setMessagesUnreadCount, addNotification } = useNotificationStore()
  const navigate = useNavigate()
  const location = useLocation()
  const isFullscreen = ['/messages', '/notifications'].includes(location.pathname)
  // Les pages détail (article, vidéo, bannière) appartiennent au Market
  const isMarketDetail = /^\/(product|video|banner|staff-banner)\//.test(location.pathname)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [downloadOpen, setDownloadOpen] = useState(false)

  // Recherche dynamique
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [showSearchDropdown, setShowSearchDropdown] = useState(false)
  const searchRef = useRef(null)

  // Debounce recherche
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([])
      setShowSearchDropdown(false)
      return
    }
    const timer = setTimeout(async () => {
      try {
        setSearchLoading(true)
        const res = await publicApiClient.get('/search', { params: { q: searchQuery, limit: 6 } })
        setSearchResults(res.data || [])
        setShowSearchDropdown(true)
      } catch {
        setSearchResults([])
      } finally {
        setSearchLoading(false)
      }
    }, 350)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Fermer le dropdown si clic extérieur
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearchDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const getResultRoute = (item) => {
    if (item.type === 1) return `/video/${encodeId(item.post_id)}`
    if (item.type === 3) return `/banner/${encodeId(item.post_id)}`
    return `/product/${encodeId(item.post_id)}`
  }
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [showMessages, setShowMessages] = useState(false)
  const [selectedLang, setSelectedLang] = useState('fr')
  const [recentMessages, setRecentMessages] = useState([])
  const [recentNotifications, setRecentNotifications] = useState([])
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [loadingNotifications, setLoadingNotifications] = useState(false)
  const [notificationsUnreadCount, setNotificationsUnreadCount] = useState(0)
  const dropdownRef = useRef(null)
  const langDropdownRef = useRef(null)
  const notificationsRef = useRef(null)
  const messagesRef = useRef(null)

  // Charger les données depuis l'API (messages/notifications)
  useEffect(() => {
    if (!user) return

    const loadData = async () => {
      // Charger les messages
      try {
        setLoadingMessages(true)
        const response = await conversationService.getConversations()
        if (response.success) {
          const convs = response.data || []
          const totalUnread = convs.reduce((sum, conv) => sum + (conv.unread_count || 0), 0)
          setMessagesUnreadCount(totalUnread)
          setRecentMessages(convs.slice(0, 3).map((c) => ({
            id: c.id,
            contact_full_name: [c.other_client?.nom, c.other_client?.prenom].filter(Boolean).join(' ') || 'Inconnu',
            contact_avatar: c.other_client?.photo_profil
              ? (c.other_client.photo_profil.startsWith('http') ? c.other_client.photo_profil : mediaUrl(c.other_client.photo_profil))
              : null,
            last_message: c.last_message?.urlArticle
              ? (c.last_message?.nomArticle || 'Article partagé')
              : (c.last_message?.message || 'Aucun message'),
            timestamp: c.last_message?.created_at ?? c.updated_at,
            unread_count: c.unread_count || 0,
          })))
        }
      } catch (error) {
        console.error('Erreur chargement messages:', error)
      } finally {
        setLoadingMessages(false)
      }

      // Charger les notifications
      try {
        setLoadingNotifications(true)
        const response = await notificationService.getNotifications()
        if (response.success) {
          const unreadNotifs = response.data.filter(n => !n.is_read)
          setNotificationsUnreadCount(unreadNotifs.length)
          setRecentNotifications(response.data.slice(0, 5))
        }
      } catch (error) {
        console.error('Erreur chargement notifications:', error)
      } finally {
        setLoadingNotifications(false)
      }
    }

    loadData()

    // Messages en temps réel : le badge se met à jour instantanément à chaque
    // nouveau message via le canal user-{id} (plus besoin de poller les messages).
    // Demander la permission des notifications navigateur (une fois)
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {})
    }

    let cleanupPusher = () => {}
    if (user?.client_id) {
      cleanupPusher = bindUserChannel(user.client_id, {
        onConversationUpdated: (data) => {
          loadData()
          // Nouveau message reçu (pas envoyé par moi)
          if (data?.is_mine !== false || !data?.sender_id) return

          const openConv = () =>
            navigate('/messages', { state: { openConversationWith: { id: data.sender_id } } })

          // Onglet en arrière-plan + permission accordée → notification navigateur (OS)
          if (typeof document !== 'undefined' && document.hidden &&
              typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            try {
              const n = new Notification('Nouveau message', {
                body: data.preview || 'Vous avez reçu un message',
                icon: '/Logo_LIGDI.png',
                tag: `conv-${data.conversation_id}`,
              })
              n.onclick = () => { window.focus(); openConv(); n.close() }
            } catch { /* repli silencieux */ }
            return
          }

          // Onglet visible → toast cliquable in-app
          toast.custom((t) => (
            <div
              onClick={() => { toast.dismiss(t.id); openConv() }}
              className="cursor-pointer bg-white shadow-lg rounded-md px-4 py-3 border-l-4 border-[#D4AF37] flex items-center gap-3 max-w-sm hover:bg-gray-50 transition-colors"
            >
              <div className="w-9 h-9 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#001F3F]">Nouveau message</p>
                <p className="text-xs text-gray-500 truncate">{data.preview || 'Vous avez reçu un message'}</p>
              </div>
            </div>
          ), { duration: 6000, position: 'top-right' })
        },
      })
    }

    // Les notifications ne sont pas diffusées via Pusher → on garde un rafraîchissement
    // périodique (plus espacé) comme repli.
    const interval = setInterval(loadData, 30000)

    const handleReloadNotifications = () => loadData()
    window.addEventListener('reloadNotifications', handleReloadNotifications)

    return () => {
      clearInterval(interval)
      cleanupPusher()
      window.removeEventListener('reloadNotifications', handleReloadNotifications)
    }
  }, [user])

  // Fermer les dropdowns si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setIsLangDropdownOpen(false)
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false)
      }
      if (messagesRef.current && !messagesRef.current.contains(event.target)) {
        setShowMessages(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = async () => {
    await logout()
    navigate(ROUTES.LOGIN)
  }

  // Nom d'affichage compatible admin (name) et client (nom/prenom)
  const displayName = user?.name || [user?.nom, user?.prenom].filter(Boolean).join(' ') || 'Utilisateur'
  const displayInitials = [
    (user?.nom || user?.name || '').charAt(0),
    (user?.prenom || '').charAt(0),
  ].filter(Boolean).join('').toUpperCase() || 'U'
  const rawPhoto = user?.photo_profil || user?.photo || null
  const displayPhoto = rawPhoto
    ? (rawPhoto.startsWith('http') ? rawPhoto
      : rawPhoto.startsWith('/storage/') ? `${storageURL.replace(/\/storage$/, '')}${rawPhoto}`
      : mediaUrl(rawPhoto))
    : null

  return (
    <div className="home-font min-h-screen bg-[#fefcf8]">
      {/* Header avec barre de recherche */}
      <header className="bg-[#FFFCF3] border-b border-amber-100/70 sticky top-0 z-50">
        <div className="w-[94%] md:w-[80%] mx-auto py-3">
          <div className="flex items-center justify-between gap-2 md:gap-4">
            {/* Logo */}
            <button
              onClick={() => navigate(ROUTES.HOME)}
              className="hover:opacity-80 transition-opacity flex-shrink-0"
            >
              <img
                src="/Logo_LIGDI.png"
                alt="LIGDI"
                className="h-12 sm:h-14 w-auto object-contain"
              />
            </button>

            {/* Navigation + Recherche + Icons — droite */}
            <div className="flex items-center gap-2 sm:gap-3">

              {/* Burger mobile */}
              <button
                className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md bg-white ring-1 ring-slate-200 transition-colors hover:bg-[#FFF8D6]"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <svg className="w-6 h-6 text-[#001F3F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileMenuOpen
                    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  }
                </svg>
              </button>

              {/* Navigation desktop */}
              <nav className="hidden lg:flex items-center gap-6 mr-2">
                {[
                  { to: ROUTES.HOME, label: 'Accueil', end: true },
                  { to: ROUTES.PRICING, label: 'Tarification' },
                ].map(({ to, label, end }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    className={({ isActive }) =>
                      isActive
                        ? 'relative inline-flex items-center gap-1.5 py-2 whitespace-nowrap text-[15px] font-semibold transition-colors after:absolute after:left-0 after:-bottom-0.5 after:h-[3px] after:rounded-full after:bg-[#F5B700] after:transition-all after:duration-300 text-[#002A4E] after:w-full'
                        : 'relative inline-flex items-center gap-1.5 py-2 whitespace-nowrap text-[15px] font-semibold transition-colors after:absolute after:left-0 after:-bottom-0.5 after:h-[3px] after:rounded-full after:bg-[#F5B700] after:transition-all after:duration-300 text-slate-600 hover:text-[#002A4E] after:w-0 hover:after:w-full'
                    }
                  >
                    {label}
                  </NavLink>
                ))}
                <NavLink
                  to={ROUTES.LIGDI}
                  className={({ isActive }) =>
                    isActive || isMarketDetail ? 'relative inline-flex items-center gap-1.5 py-2 whitespace-nowrap text-[15px] font-semibold transition-colors after:absolute after:left-0 after:-bottom-0.5 after:h-[3px] after:rounded-full after:bg-[#F5B700] after:transition-all after:duration-300 text-[#002A4E] after:w-full' : 'relative inline-flex items-center gap-1.5 py-2 whitespace-nowrap text-[15px] font-semibold transition-colors after:absolute after:left-0 after:-bottom-0.5 after:h-[3px] after:rounded-full after:bg-[#F5B700] after:transition-all after:duration-300 text-slate-600 hover:text-[#002A4E] after:w-0 hover:after:w-full'
                  }
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                  KUDHI Market
                </NavLink>
                <button type="button" onClick={() => setDownloadOpen(true)} className="relative inline-flex items-center gap-1.5 py-2 whitespace-nowrap text-[15px] font-semibold transition-colors after:absolute after:left-0 after:-bottom-0.5 after:h-[3px] after:rounded-full after:bg-[#F5B700] after:transition-all after:duration-300 text-slate-600 hover:text-[#002A4E] after:w-0 hover:after:w-full">
                  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" /></svg>
                  Télécharger l'app
                </button>
              </nav>

              {/* Barre de recherche - Desktop */}
              <div className="hidden md:flex w-56 lg:w-72 relative" ref={searchRef}>
                <div className="relative w-full">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => searchQuery.trim() && setShowSearchDropdown(true)}
                    placeholder="Rechercher…"
                    className="w-full px-3 py-2.5 pl-9 rounded-md bg-white border border-slate-200 text-sm text-[#002A4E] placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-300"
                  />
                  <div className="absolute left-3 top-1/2 -translate-y-1/2">
                    {searchLoading
                      ? <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      : <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    }
                  </div>
                </div>

                {/* Dropdown résultats */}
                {showSearchDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-md shadow-xl border border-gray-200 z-50 max-h-80 overflow-y-auto">
                    {searchResults.length === 0 ? (
                      <p className="px-4 py-3 text-sm text-gray-500 text-center">Aucun résultat pour « {searchQuery} »</p>
                    ) : (
                      <>
                        {searchResults.map((item) => (
                          <button
                            key={item.post_id}
                            onClick={() => {
                              navigate(getResultRoute(item))
                              setShowSearchDropdown(false)
                              setSearchQuery('')
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors text-left border-b border-gray-100 last:border-0"
                          >
                            {/* Miniature */}
                            {item.first_image?.chemin ? (
                              <img
                                src={mediaUrl(item.first_image.chemin)}
                                alt=""
                                className="w-10 h-10 rounded object-cover flex-shrink-0"
                                onError={(e) => { e.target.style.display = 'none' }}
                              />
                            ) : (
                              <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{item.libele}</p>
                              <p className="text-xs text-gray-500">
                                {item.type === 1 ? 'Vidéo' : item.type === 3 ? 'Bannière' : 'Article'}
                                {item.prix ? ` · ${parseFloat(item.prix).toLocaleString('fr-FR')} FCFA` : ''}
                              </p>
                            </div>
                          </button>
                        ))}
                        <div className="px-3 py-2 bg-gray-50 text-center">
                          <p className="text-xs text-gray-500">{searchResults.length} résultat(s) trouvé(s)</p>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Mon Espace */}
              <NavLink
                to={ROUTES.DASHBOARD}
                className="hidden lg:flex items-center gap-2 px-4 py-2.5 bg-[#FFD700] hover:bg-[#F5C800] text-[#002A4E] font-bold rounded-md shadow-sm shadow-amber-300/60 transition-colors whitespace-nowrap text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Mon Espace
              </NavLink>

              {/* Bouton recherche - Mobile */}
              <button
                onClick={() => setShowSearch(true)}
                className="hidden w-9 h-9 sm:w-11 sm:h-11 items-center justify-center bg-yellow-100 hover:bg-yellow-200 rounded-full transition-colors"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-darkBlue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
              {/* Messages */}
              <div className="relative" ref={messagesRef}>
                <button 
                  onClick={() => setShowMessages(!showMessages)}
                  className="relative w-10 h-10 flex items-center justify-center rounded-md bg-white ring-1 ring-slate-200 text-[#002A4E] hover:ring-amber-300 hover:bg-[#FFF8D6] transition-colors"
                >
                  <svg className="w-[22px] h-[22px] text-[#002A4E]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                  {messagesUnreadCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold ring-2 ring-[#FFFCF3]">
                      {messagesUnreadCount > 99 ? '99+' : messagesUnreadCount}
                    </span>
                  )}
                </button>

                {/* Modal Messages */}
                {showMessages && (
                  <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-md shadow-[0_20px_50px_-15px_rgba(0,42,78,0.35)] ring-1 ring-slate-200 z-50 max-h-[28rem] overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/70">
                      <h3 className="flex items-center gap-2 font-extrabold text-[#002A4E]"><span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#FFD700]"><svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg></span>Messages{messagesUnreadCount > 0 && <span className="rounded-md bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{messagesUnreadCount}</span>}</h3>
                      <button 
                        onClick={() => {
                          navigate('/messages')
                          setShowMessages(false)
                        }}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-semibold text-[#B8860B] hover:bg-[#FFF8D6]"
                      >
                        Tout voir
                      </button>
                    </div>
                    <div className="overflow-y-auto max-h-[23rem] p-1.5">
                      {loadingMessages ? (
                        <div className="p-8 flex items-center justify-center">
                          <div className="animate-spin rounded-full h-7 w-7 border-2 border-[#F5B700] border-t-transparent"></div>
                        </div>
                      ) : recentMessages.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                          <p className="text-sm">Aucun message récent</p>
                        </div>
                      ) : (
                        recentMessages.map((conv) => {
                          const avatar = conv.contact_avatar
                          const avatarUrl = avatar 
                            ? (avatar.startsWith('http') ? avatar : `http://localhost/ligdi/public/${avatar}`)
                            : avatarPlaceholder
                          
                          return (
                            <button 
                              key={conv.id} 
                              onClick={() => {
                                navigate('/messages')
                                setShowMessages(false)
                              }}
                              className="w-full rounded-md p-3 hover:bg-[#FFF8D6] transition-colors text-left"
                            >
                              <div className="flex items-start gap-3">
                                <img src={avatarUrl} alt={conv.contact_full_name} className="w-10 h-10 flex-shrink-0 rounded-full object-cover ring-2 ring-white" />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="font-bold text-sm text-[#002A4E] truncate">{conv.contact_full_name}</span>
                                    <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{formatRelativeTime(conv.timestamp) || 'Récent'}</span>
                                  </div>
                                  <p className={`text-sm line-clamp-2 ${conv.unread_count > 0 ? 'font-semibold text-[#002A4E]' : 'text-slate-500'}`}>{conv.last_message || 'Aucun message'}</p>
                                  {conv.unread_count > 0 && (
                                    <span className="inline-block mt-1 px-1.5 py-0.5 bg-[#FFD700] text-[#002A4E] text-[10px] font-bold rounded-md">
                                      {conv.unread_count} nouveau{conv.unread_count > 1 ? 'x' : ''}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </button>
                          )
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Notifications */}

              <div className="relative" ref={notificationsRef}>
                <button 
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative w-10 h-10 flex items-center justify-center rounded-md bg-white ring-1 ring-slate-200 text-[#002A4E] hover:ring-amber-300 hover:bg-[#FFF8D6] transition-colors"
                >
                  <svg className="w-[22px] h-[22px] text-[#002A4E]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
                  </svg>
                  {notificationsUnreadCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold ring-2 ring-[#FFFCF3]">
                      {notificationsUnreadCount > 99 ? '99+' : notificationsUnreadCount}
                    </span>
                  )}
                </button>

                {/* Modal Notifications */}

                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-md shadow-[0_20px_50px_-15px_rgba(0,42,78,0.35)] ring-1 ring-slate-200 z-50 max-h-[28rem] overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/70">
                      <h3 className="flex items-center gap-2 font-extrabold text-[#002A4E]"><span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#FFD700]"><svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" /></svg></span>Notifications{notificationsUnreadCount > 0 && <span className="rounded-md bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{notificationsUnreadCount}</span>}</h3>
                      <button 
                        onClick={() => {
                          navigate('/notifications')
                          setShowNotifications(false)
                        }}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-semibold text-[#B8860B] hover:bg-[#FFF8D6]"
                      >
                        Tout voir
                      </button>
                    </div>
                    <div className="overflow-y-auto max-h-[23rem] p-1.5">
                      {loadingNotifications ? (
                        <div className="p-8 flex items-center justify-center">
                          <div className="animate-spin rounded-full h-7 w-7 border-2 border-[#F5B700] border-t-transparent"></div>
                        </div>
                      ) : recentNotifications.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                          <p className="text-sm">Aucune notification</p>
                        </div>
                      ) : (
                        recentNotifications.map((notif) => (
                          <button 
                            key={notif.id} 
                            onClick={() => {
                              navigate('/notifications')
                              setShowNotifications(false)
                            }}
                            className={`w-full rounded-md p-3 hover:bg-[#FFF8D6] transition-colors text-left ${
                              !notif.is_read ? 'bg-[#FFFDF3]' : ''
                            }`}
                          >
                          <div className="flex items-start gap-3">
                            {renderNotifIcon(notif.type)}
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-bold text-[#002A4E] mb-0.5">{notif.title}</h4>
                              <p className="text-xs text-gray-600 mb-1 line-clamp-2">{notif.message}</p>
                              <span className="text-xs text-gray-400">{formatRelativeTime(notif.timestamp || notif.created_at)}</span>
                              {!notif.is_read && (
                                <span className="ml-2 inline-block w-2 h-2 bg-[#F5B700] rounded-full"></span>
                              )}
                            </div>
                          </div>
                        </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Avatar avec dropdown */}

              <div className="relative" ref={dropdownRef}>
                <button 
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="hover:opacity-80 transition-opacity"
                >
                  {displayPhoto ? (
                    <img
                      src={displayPhoto}
                      alt=""
                      className="w-10 h-10 rounded-md object-cover ring-2 ring-[#FFD700]"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }}
                    />
                  ) : null}
                  <div
                    className="w-10 h-10 rounded-md bg-[#002A4E] items-center justify-center ring-2 ring-[#FFD700] select-none"
                    style={{ display: displayPhoto ? 'none' : 'flex' }}
                  >
                    <span className="text-[#FFD700] text-sm font-bold tracking-wide">
                      {displayInitials}
                    </span>
                  </div>
                </button>

                {/* Dropdown menu */}

                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-60 bg-white rounded-md shadow-[0_20px_50px_-15px_rgba(0,42,78,0.35)] ring-1 ring-slate-200 p-1.5 z-50">
                    {/* User info header */}
                    <div className="px-3 py-3 mb-1 border-b border-slate-100">
                      <p className="text-sm font-bold text-[#002A4E] truncate">{displayName}</p>
                      <p className="text-xs text-gray-500 truncate">{user?.email || ''}</p>
                    </div>
                    <button
                      onClick={() => {
                        navigate(ROUTES.DASHBOARD)
                        setIsDropdownOpen(false)
                      }}
                      className="w-full rounded-md px-3 py-2.5 text-left text-sm font-medium text-[#002A4E] hover:bg-[#FFF8D6] flex items-center gap-3 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
                      </svg>
                      Mon Dashboard
                    </button>
                    <button
                      onClick={() => {
                        navigate(ROUTES.PROFILE)
                        setIsDropdownOpen(false)
                      }}
                      className="w-full rounded-md px-3 py-2.5 text-left text-sm font-medium text-[#002A4E] hover:bg-[#FFF8D6] flex items-center gap-3 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Mon Profil
                    </button>
                    <button
                      onClick={() => {
                        navigate('/settings')
                        setIsDropdownOpen(false)
                      }}
                      className="w-full rounded-md px-3 py-2.5 text-left text-sm font-medium text-[#002A4E] hover:bg-[#FFF8D6] flex items-center gap-3 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Paramètres
                    </button>
                    <button
                      onClick={() => {
                        navigate('/help')
                        setIsDropdownOpen(false)
                      }}
                      className="w-full rounded-md px-3 py-2.5 text-left text-sm font-medium text-[#002A4E] hover:bg-[#FFF8D6] flex items-center gap-3 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Aide
                    </button>
                    <hr className="my-1.5 border-slate-100" />
                    <button
                      onClick={handleLogout}
                      className="w-full rounded-md px-3 py-2.5 text-left text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Déconnexion
                    </button>
                  </div>
                )}
              </div>

              {/* Sélecteur de langue avec dropdown */}
              <div className="relative hidden sm:block" ref={langDropdownRef}>
                {/* <button 
                  onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                  className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <span className="text-sm sm:text-base font-semibold text-darkBlue">{selectedLang === 'fr' ? 'FR' : 'EN'}</span>
                  <span className="text-xs sm:text-sm text-gray-500 hidden md:inline">{selectedLang === 'fr' ? 'Fr' : 'En'}</span>
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 text-darkBlue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button> */}

                {/* Dropdown menu langues */}
                {/* {isLangDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg border border-gray-200 py-2 z-50">
                    <button
                      onClick={() => {
                        setSelectedLang('fr')
                        setIsLangDropdownOpen(false)
                      }}
                      className={`w-full px-4 py-2 text-left text-sm flex items-center gap-3 transition-colors ${
                        selectedLang === 'fr' ? 'bg-gray-50 text-darkBlue font-semibold' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="font-semibold">FR</span>
                      <span>Français</span>
                    </button>
                    <button
                      onClick={() => {
                        setSelectedLang('en')
                        setIsLangDropdownOpen(false)
                      }}
                      className={`w-full px-4 py-2 text-left text-sm flex items-center gap-3 transition-colors ${
                        selectedLang === 'en' ? 'bg-gray-50 text-darkBlue font-semibold' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="font-semibold">GB</span>
                      <span>English</span>
                    </button>
                  </div>
                )} */}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Menu mobile */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-b border-gray-200 shadow-lg z-40">
          <nav className="flex flex-col divide-y divide-gray-100">
            {[
              { to: ROUTES.HOME, label: 'Accueil', end: true },
              { to: ROUTES.PRICING, label: 'Tarification' },
              { to: ROUTES.LIGDI, label: 'KUDHI Market' },
            ].map(({ to, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `px-6 py-4 text-base font-medium transition-colors ${isActive ? 'text-[#D4AF37] bg-yellow-50' : 'text-[#001F3F] hover:bg-gray-50'}`
                }
              >
                {label}
              </NavLink>
            ))}
            <button
              type="button"
              onClick={() => { setMobileMenuOpen(false); setDownloadOpen(true) }}
              className="px-6 py-4 text-left text-base font-medium text-[#001F3F] hover:bg-gray-50 transition-colors"
            >
              Télécharger l'app
            </button>
            <NavLink
              to={ROUTES.DASHBOARD}
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `px-6 py-4 text-base font-medium transition-colors ${isActive ? 'text-[#D4AF37] bg-yellow-50' : 'text-[#001F3F] hover:bg-gray-50'}`
              }
            >
              Mon Espace
            </NavLink>
            <button
              onClick={() => { handleLogout(); setMobileMenuOpen(false) }}
              className="px-6 py-4 text-base font-medium text-red-600 hover:bg-red-50 transition-colors text-left"
            >
              Déconnexion
            </button>
          </nav>
        </div>
      )}

      {/* Barre de recherche étendue - Mobile */}

      {showSearch && (
        <div className="bg-white border-b border-gray-200 sticky top-[65px] z-40 md:hidden">
          <div className="flex items-center gap-3 p-4">
            <button 
              onClick={() => setShowSearch(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
            >
              <svg className="w-6 h-6 text-darkBlue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Rechercher…"
                autoFocus
                className="w-full px-4 py-2.5 pr-12 rounded-full bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
              />
              <button className="absolute right-3 top-1/2 -translate-y-1/2">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contenu principal */}
      <main>
        <Outlet />
        <DownloadAppModal open={downloadOpen} onClose={() => setDownloadOpen(false)} />
      </main>

      {!isFullscreen && <Footer />}
    </div>
  )
}

export default DashboardLayout
